import { useEffect, useState } from 'react'
import { Shield, RefreshCw, Search, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface CheckResult { score: number; present?: boolean; valid?: boolean; [k: string]: any }
interface DomainResult {
  domain: string
  overall: number
  grade: string
  checks: Record<string, CheckResult>
  checked_at: string
}

const GRADE_TONE: Record<string, string> = {
  A: 'text-emerald bg-emerald/15 border-emerald/40',
  B: 'text-blue-light bg-blue/15 border-blue/40',
  C: 'text-amber-light bg-amber/15 border-amber/40',
  D: 'text-orange bg-orange/15 border-orange/40',
  F: 'text-rose bg-rose/15 border-rose/40',
}

export default function DnsPosturePage() {
  const [data, setData] = useState<{ domains: DomainResult[]; summary: any } | null>(null)
  const [loading, setLoading] = useState(true)
  const [overrideDomain, setOverrideDomain] = useState('')

  const fetchAll = (domain?: string) => {
    setLoading(true)
    api.get('/api/cyber/dns-posture', { params: domain ? { domain } : {}, timeout: 60_000 })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchAll() }, [])

  const labelFor: Record<string, string> = {
    spf: 'SPF', dmarc: 'DMARC', dkim: 'DKIM', dnssec: 'DNSSEC',
    mx: 'MX', caa: 'CAA', https: 'HTTPS+HSTS', tls: 'TLS cert',
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Shield size={24} className="text-emerald" /> DNS / TLS Posture
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            DMARC · SPF · DKIM · DNSSEC · MX · CAA · HSTS · TLS cert chain — graded A-F per monitored domain.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted" />
            <input value={overrideDomain} onChange={e => setOverrideDomain(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchAll(overrideDomain || undefined)}
              placeholder="check ad-hoc domain"
              className="input pl-7 py-1.5 text-xs w-56" />
          </div>
          <button type="button" onClick={() => fetchAll(overrideDomain || undefined)} className="btn-secondary text-xs">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Score
          </button>
        </div>
      </div>

      {loading && <div className="card p-6 text-center text-text-muted text-sm">Querying DNS + TLS… (10-30s)</div>}

      {data && data.domains.length === 0 && !loading && (
        <div className="card p-6 text-center text-text-muted text-sm">
          No monitored domains configured. Set MONITORED_DOMAINS env var or use the search above.
        </div>
      )}

      {data && data.domains.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="card p-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Average score</div>
              <div className="text-3xl font-bold text-text-primary mt-1 font-mono">{data.summary.avg}/100</div>
            </div>
            <div className="card p-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Grade distribution</div>
              <div className="flex gap-2 mt-2 text-xs">
                {Object.entries(data.summary.grades).map(([g, n]: any) => (
                  <span key={g} className={clsx('px-2 py-0.5 rounded border text-[11px] font-mono font-bold', GRADE_TONE[g])}>
                    {g}: {n}
                  </span>
                ))}
              </div>
            </div>
            <div className="card p-3">
              <div className="text-[10px] uppercase tracking-wider text-text-muted">Domains checked</div>
              <div className="text-3xl font-bold text-text-primary mt-1 font-mono">{data.summary.total}</div>
            </div>
          </div>

          {data.domains.map(d => (
            <div key={d.domain} className="card p-4">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <h2 className="text-sm font-bold text-text-primary font-mono">{d.domain}</h2>
                  <p className="text-[10px] text-text-muted">checked {new Date(d.checked_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-mono font-bold text-text-primary">{d.overall}/100</span>
                  <span className={clsx('px-3 py-1 rounded-md border text-xl font-mono font-bold', GRADE_TONE[d.grade])}>
                    {d.grade}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {Object.entries(d.checks).map(([key, c]) => {
                  const ok = c.score >= 70
                  const Icon = ok ? CheckCircle2 : c.score > 0 ? AlertTriangle : XCircle
                  const tone = ok ? 'text-emerald' : c.score > 0 ? 'text-amber-light' : 'text-rose'
                  return (
                    <div key={key} className="rounded-md border border-border p-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] uppercase tracking-wider text-text-muted">{labelFor[key] || key}</span>
                        <Icon size={11} className={tone} />
                      </div>
                      <div className={clsx('text-base font-mono font-bold mt-0.5', tone)}>{c.score}</div>
                      {key === 'spf' && c.policy && <div className="text-[9px] text-text-muted">policy: {c.policy}</div>}
                      {key === 'dmarc' && c.policy && <div className="text-[9px] text-text-muted">p={c.policy} {c.pct ? `pct=${c.pct}` : ''}</div>}
                      {key === 'dkim' && c.selectors && <div className="text-[9px] text-text-muted">{c.selectors.length ? c.selectors.join(', ') : 'no selectors found'}</div>}
                      {key === 'tls' && (c as any).days_left != null && <div className="text-[9px] text-text-muted">{(c as any).days_left}d left</div>}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
